gsap.registerPlugin(ScrollTrigger);

/* =====================================================
   UTILITY — mobile distance scaling
   ===================================================== */

/**
 * Scales an animation distance down on mobile to keep transforms cheap.
 * @param {number} value
 * @param {object} options - preset options (may contain .mobile)
 * @returns {number}
 */
function md(value, options) {
  if (!options || !options.mobile) return value;
  var mobileScale = options.mobileDistanceScale != null ? options.mobileDistanceScale : 0.55;
  return value * mobileScale;
}

function optionValue(options, key, fallback) {
  return options && options[key] != null ? options[key] : fallback;
}

var DEFAULT_TIMING = {
  enterDelay: 0.1,
  enterDuration: 0.8,
  exitOffset: 0,
  exitDuration: 0.9
};

var DEBUG_SCROLLTRIGGER = false;
var DEBUG_SECTION_MARKERS = [];
var DEBUG_TIMELINE_PANEL = null;
var DEBUG_TIMELINE_PIP_WINDOW = null;

/* =====================================================
   PRESETS REGISTRY — Reusable named animation presets
   Signature: (tl, target, position, duration, options)
   ===================================================== */

var PRESETS = {
  /** fade-up: enter from below with fade */
  "fade-up": function (tl, target, position, duration, options) {
    var distance = md((options && options.distance) || 40, options);
    var motionDuration = optionValue(options, "enterDuration", duration);
    var ease = (options && options.ease) || "power2.out";
    gsap.set(target, { yPercent: distance, opacity: 0 });
    tl.to(target, { yPercent: 0, opacity: 1, ease: ease, duration: motionDuration }, position);
  },

  /**
   * staged-fade-up: reveals groups of children one after another, each rising
   * from below with a fade. `phases` is a list of selectors resolved inside the
   * section; when a selector matches several nodes they share an inner stagger.
   */
  "staged-fade-up": function (tl, target, position, duration, options) {
    var phases = optionValue(options, "phases", []);
    var distance = md(optionValue(options, "distance", 42), options);
    var motionDuration = optionValue(options, "enterDuration", duration);
    var phaseStagger = optionValue(options, "phaseStagger", 0.28);
    var itemStagger = md(optionValue(options, "itemStagger", 0.08), options);
    var ease = optionValue(options, "ease", "power2.out");

    // The section wrapper starts hidden; reveal it before the children move.
    if (options && options.mobile) {
      tl.set(target, { opacity: 1 }, position);
    } else {
      gsap.set(target, { autoAlpha: 1 });
    }

    phases.forEach(function (selector, phaseIndex) {
      var nodes = target.querySelectorAll(selector);
      if (!nodes.length) {
        console.warn("[AnimEngine] staged-fade-up: " + selector + " not found");
        return;
      }

      gsap.set(nodes, { y: distance, autoAlpha: 0 });
      tl.to(nodes, {
        y: 0,
        autoAlpha: 1,
        ease: ease,
        duration: motionDuration,
        stagger: nodes.length > 1 ? itemStagger : 0
      }, position + (phaseIndex * phaseStagger));
    });
  },

  /** bio-columns-enter: left and right bio content enter in three ordered phases */
  "bio-columns-enter": function (tl, target, position, duration, options) {
    var title = target.querySelector(".bio__title");
    var texts = target.querySelectorAll(".bio__text");
    var logos = target.querySelector(".bio__content--wrapper > img");
    var largeImage = target.querySelector(".grid-item--large");
    var shortImage = target.querySelector(".grid-item--short");
    var lastImage = target.querySelector(".grid-item-short-last");
    var phaseStagger = optionValue(options, "bioGroupStagger", 0.12);
    var ease = optionValue(options, "bioEnterEase", "power3.out");

    if (options && options.mobile) {
      tl.set(target, { opacity: 1 }, position);
    } else {
      gsap.set(target, { autoAlpha: 1 });
    }

    function enterFromLeft(elements, phasePosition) {
      if (!elements || !elements.length && !elements.nodeType) return;
      tl.fromTo(elements,
        { xPercent: -100, autoAlpha: 0 },
        { xPercent: 0, autoAlpha: 1, ease: ease, duration: duration },
        phasePosition
      );
    }

    function enterFromRight(elements, phasePosition) {
      if (!elements || !elements.length && !elements.nodeType) return;
      tl.fromTo(elements,
        { xPercent: 100, autoAlpha: 0 },
        { xPercent: 0, autoAlpha: 1, ease: ease, duration: duration },
        phasePosition
      );
    }

    // Phase 1: title and main image.
    enterFromLeft(title, position);
    enterFromRight(largeImage, position);

    // Phase 2: body copy and first short image.
    enterFromLeft(texts, position + phaseStagger);
    enterFromRight(shortImage, position + phaseStagger);

    // Phase 3: logos and final short image.
    enterFromLeft(logos, position + (phaseStagger * 2));
    enterFromRight(lastImage, position + (phaseStagger * 2));
  },


  /** before-after-enter: title first, then diagonal cards from both sides */
  "before-after-enter": function (tl, target, position, duration, options) {
    var title = target.querySelector(".before-after__title");
    var beforeCard = target.querySelector(".before-after__card--before");
    var afterCard = target.querySelector(".before-after__card--after");
    var arrow = target.querySelector(".before-after__arrow");
    var cardDelay = optionValue(options, "beforeAfterCardDelay", 0.12);
    var arrowDuration = optionValue(options, "beforeAfterArrowDuration", 0.18);
    var titleOffset = optionValue(options, "beforeAfterTitleOffset", -45);
    var cardXOffset = optionValue(options, "beforeAfterCardXOffset", 45);
    var cardYOffset = optionValue(options, "beforeAfterCardYOffset", 45);
    var ease = optionValue(options, "beforeAfterEnterEase", "power3.out");

    if (options && options.mobile) {
      tl.set(target, { opacity: 1 }, position);
    } else {
      gsap.set(target, { autoAlpha: 1 });
    }

    if (title) {
      tl.fromTo(title,
        { yPercent: titleOffset, autoAlpha: 0 },
        { yPercent: 0, autoAlpha: 1, ease: ease, duration: duration },
        position
      );
    }

    if (beforeCard) {
      tl.fromTo(beforeCard,
        { xPercent: -cardXOffset, yPercent: cardYOffset, autoAlpha: 0 },
        { xPercent: 0, yPercent: 0, autoAlpha: 1, ease: ease, duration: duration },
        position + cardDelay
      );
    }

    if (afterCard) {
      tl.fromTo(afterCard,
        { xPercent: cardXOffset, yPercent: cardYOffset, autoAlpha: 0 },
        { xPercent: 0, yPercent: 0, autoAlpha: 1, ease: ease, duration: duration },
        position + cardDelay
      );
    }

    // Final phase: reveal the connector arrow after both cards have settled.
    if (arrow) {
      tl.fromTo(arrow,
        { autoAlpha: 0 },
        { autoAlpha: 1, ease: ease, duration: arrowDuration },
        position + cardDelay + duration
      );
    }
  },

  /** scale-out: shrink from natural size to zero while fading */
  "scale-out": function (tl, target, position, duration, options) {
    var endScale = optionValue(options, "exitScale", 0);
    var motionDuration = optionValue(options, "exitDuration", duration);
    var fadeDelayRatio = Math.max(0, Math.min(1, optionValue(options, "exitFadeDelayRatio", 0)));
    var ease = optionValue(options, "exitEase", "power2.in");

    tl.to(target, {
      scale: endScale,
      transformOrigin: "center center",
      ease: ease,
      duration: motionDuration
    }, position);

    tl.to(target, {
      autoAlpha: 0,
      ease: ease,
      duration: motionDuration * (1 - fadeDelayRatio)
    }, position + (motionDuration * fadeDelayRatio));
  },

  "fade-down": function (tl, target, position, duration, options) {
    var distance = md((options && options.distance) || 40, options);
    var motionDuration = optionValue(options, "exitDuration", duration);
    var fadeDelayRatio = optionValue(options, "exitFadeDelayRatio", 0);
    var ease = (options && options.ease) || "power2.in";

    tl.to(target, {
      yPercent: -distance,
      ease: ease,
      duration: motionDuration
    }, position);

    tl.to(target, {
      autoAlpha: 0,
      ease: ease,
      duration: motionDuration * (1 - fadeDelayRatio)
    }, position + motionDuration * fadeDelayRatio);
  },

  /** reality-container-exit: subtle upward fade for the reality content only */
  "reality-container-exit": function (tl, target, position, duration, options) {
    var container = target.querySelector(".reality__container");
    if (!container) {
      console.warn("[AnimEngine] .reality__container not found");
      return;
    }

    var distance = md((options && options.distance) || 8, options);
    var motionDuration = optionValue(options, "exitDuration", optionValue(options, "motionDuration", 0.45));
    var ease = (options && options.ease) || "power2.out";

    tl.to(container, {
      yPercent: -distance,
      autoAlpha: 0,
      ease: ease,
      duration: motionDuration
    }, position);
  },

  /** visibility-container-enter: reveal after the first 60% of the entrance */
  "visibility-container-enter": function (tl, target, position, duration, options) {
    var container = target.querySelector(".visibility__container");
    if (!container) {
      console.warn("[AnimEngine] .visibility__container not found");
      return;
    }

    var stage = document.querySelector(".stage");

    if (options && options.mobile) {
      // The mobile builder hides every non-hero section initially, so reveal
      // the parent before animating its inner visibility container.
      tl.set(target, { opacity: 1 }, position);
    } else {
      gsap.set(target, { autoAlpha: 1 });
    }

    var startViewport = (options && options.startViewport) || 0.6;
    var motionDuration = (options && options.motionDuration) || duration;
    var fadeDelayRatio = (options && options.fadeDelayRatio) || 0.6;
    var ease = (options && options.ease) || "sine.inOut";

    var startY = function () {
      var stageHeight = stage ? stage.clientHeight : window.innerHeight;
      return stageHeight * startViewport;
    };

    // Move upward across the full entrance while remaining hidden initially.
    tl.fromTo(container,
      {
        y: startY,
        autoAlpha: 0
      },
      {
        y: 0,
        autoAlpha: 0,
        ease: ease,
        duration: motionDuration
      },
      position
    );

    // Fade in during the movement so the upward trajectory remains visible.
    tl.to(container, {
      autoAlpha: 1,
      ease: ease,
      duration: motionDuration * (1 - fadeDelayRatio),
      overwrite: "auto"
    }, position + motionDuration * fadeDelayRatio);
  },

  /** visibility-container-exit: zoom the content forward and fade it out */
  "visibility-container-exit": function (tl, target, position, duration, options) {
    var container = target.querySelector(".visibility__container");
    if (!container) {
      console.warn("[AnimEngine] .visibility__container not found");
      return;
    }

    var endScale = (options && options.exitScale) || 2.2;
    var motionDuration = (options && options.exitDuration) || 0.8;
    var exitFadeDelayRatio = (options && options.exitFadeDelayRatio) || 0;
    var ease = (options && options.exitEase) || "power2.in";

    // Zoom first while keeping the content visible.
    tl.to(container, {
      scale: endScale,
      transformOrigin: "center center",
      ease: ease,
      duration: motionDuration
    }, position);

    // Start fading according to the configured point in the exit animation.
    tl.to(container, {
      autoAlpha: 0,
      ease: ease,
      duration: motionDuration * (1 - exitFadeDelayRatio)
    }, position + motionDuration * exitFadeDelayRatio);
  },

  /** overview-container-enter: subtle zoom from a small state to full size */
  "overview-container-enter": function (tl, target, position, duration, options) {
    var container = target.querySelector(".overview__container");
    if (!container) {
      console.warn("[AnimEngine] .overview__container not found");
      return;
    }

    var startScale = (options && options.startScale) || 0.45;
    var motionDuration = (options && options.enterDuration) || duration;
    var startDelayRatio = (options && options.startDelayRatio) || 0;
    var ease = (options && options.enterEase) || "power2.out";
    var enterPosition = position + motionDuration * startDelayRatio;

    if (options && options.mobile) {
      // Reveal the section parent before the container's scale/opacity tween.
      tl.set(target, { opacity: 1 }, enterPosition);
    } else {
      gsap.set(target, { autoAlpha: 1 });
    }

    tl.fromTo(container,
      {
        scale: startScale,
        autoAlpha: 0,
        transformOrigin: "center center"
      },
      {
        scale: 1,
        autoAlpha: 1,
        ease: ease,
        duration: motionDuration
      },
      enterPosition);
  },

  /** fade-in: enter with pure opacity (no movement) */
  "fade-in": function (tl, target, position, duration, options) {
    var motionDuration = optionValue(options, "enterDuration", duration);
    var ease = (options && options.ease) || "power1.out";
    gsap.set(target, { opacity: 0 });
    tl.to(target, { opacity: 1, ease: ease, duration: motionDuration }, position);
  },

  /** fade-out: simple opacity fade to 0 (exit) */
  "fade-out": function (tl, target, position, duration, options) {
    var motionDuration = optionValue(options, "exitDuration", duration);
    var fadeDelayRatio = optionValue(options, "exitFadeDelayRatio", 0);
    var ease = (options && options.ease) || "power2.in";

    tl.to(target, {
      autoAlpha: 0,
      ease: ease,
      duration: motionDuration * (1 - fadeDelayRatio)
    }, position + motionDuration * fadeDelayRatio);
  },

  /** scale-in: enter by scaling up from a small size with fade */
  "scale-in": function (tl, target, position, duration, options) {
    var startScale = (options && options.startScale) || 0.45;
    var motionDuration = optionValue(options, "enterDuration", duration);
    var ease = (options && options.ease) || "power3.out";
    gsap.set(target, { scale: startScale, opacity: 0 });
    tl.to(target, { scale: 1, opacity: 1, ease: ease, duration: motionDuration }, position);
  },

  /** shatter-out: exit by scattering child items to their offsets */
  "shatter-out": function (tl, target, position, duration, options) {
    var itemSelector = (options && options.itemSelector) || ".item";
    var itemOffsets = (options && options.itemOffsets) || [];
    var stagger = (options && options.stagger) || 0.04;
    var ease = (options && options.ease) || "power2.in";
    var items = target.querySelectorAll(itemSelector);

    items.forEach(function (item, i) {
      var offset = itemOffsets[i] || { xPercent: 0, yPercent: 120 };
      tl.to(item, {
        xPercent: md(offset.xPercent, options),
        yPercent: md(offset.yPercent, options),
        opacity: 0,
        ease: ease,
        duration: duration
      }, position + (i * stagger));
    });

    // Also animate out any non-item elements (e.g. a header) so nothing lingers
    if (options && options.headerSelector) {
      var headerEl = target.querySelector(options.headerSelector);
      if (headerEl) {
        tl.to(headerEl, {
          yPercent: md(-60, options),
          opacity: 0,
          ease: ease,
          duration: duration
        }, position);
      }
    }
  },

  /** custom-hero: hero exit — scatters logo/text/media/thumbnail + bg parallax */
  "custom-hero": function (tl, target, position, duration, options) {
    var logo = target.querySelector(".hero__logo");
    var text = target.querySelector(".hero__text");
    var media = target.querySelector(".hero__media");
    var thumbnail = target.querySelector(".hero__thumbnail");
    var mobile = options && options.mobile;
    var compact = window.matchMedia("(max-width: 1200px)").matches;
    var useCompactExit = mobile || compact;

    // CSS switches the hero to a stacked composition at 1200px, so use the
    // calmer scatter there too. Desktop-wide keeps the original motion.
    // En movil el logo va centrado arriba (no abajo a la izquierda como en
    // desktop): con el recorrido compacto se quedaba asomado en una esquina
    // durante toda la navegacion, asi que aqui sale hacia arriba y se apaga.
    if (logo) tl.to(logo, {
      xPercent: mobile ? -60 : (useCompactExit ? -180 : -400),
      yPercent: mobile ? -320 : (useCompactExit ? -80 : -160),
      scale: mobile ? 1.6 : (useCompactExit ? 2 : 4),
      opacity: mobile ? 0 : 1,
      ease: "none",
      duration: duration
    }, position);
    if (text) tl.to(text, {
      xPercent: useCompactExit ? -150 : -260,
      yPercent: useCompactExit ? 60 : 90,
      scale: useCompactExit ? 1.3 : 2,
      ease: "none",
      duration: duration
    }, position);
    if (media) tl.to(media, {
      xPercent: useCompactExit ? 125 : 180,
      scale: useCompactExit ? 1.35 : 2,
      ease: "none",
      duration: duration
    }, position);
    if (thumbnail) tl.to(thumbnail, {
      xPercent: useCompactExit ? 130 : 180,
      scale: useCompactExit ? 1.3 : 2,
      opacity: 0,
      ease: "none",
      duration: duration
    }, position);

    // Background parallax zoom on the hero background layer
    var bgImg = document.querySelector(".stage__bg--hero img");
    if (bgImg) tl.to(bgImg, {
      scale: useCompactExit ? 1.35 : 2,
      ease: "none",
      duration: duration
    }, position);

    // Remove any residual hero content before video becomes fully visible.
    if (!mobile) {
      tl.to(target, {
        autoAlpha: 0,
        ease: "power1.in",
        duration: 0.12
      }, position + duration - 0.12);
    }
  },

  /** video-enter: video section enter — player scales, header/btn fade up staggered */
  "video-enter": function (tl, target, position, duration, options) {
    var player = target.querySelector(".video__player");
    var header = target.querySelector(".video__header");
    var btn = target.querySelector(".video__btn");

    if (options && options.mobile) {
      // Keep the section hidden until its entrance position is reached.
      tl.set(target, { opacity: 1 }, position);
    } else {
      gsap.set(target, { autoAlpha: 1 });
    }
    if (player) gsap.set(player, { scale: 0.45, opacity: 0 });
    if (header) gsap.set(header, { yPercent: md(30, options), opacity: 0 });
    if (btn) gsap.set(btn, { yPercent: md(40, options), opacity: 0 });

    if (player) tl.to(player, { scale: 1, opacity: 1, ease: "power3.out", duration: duration * 0.8 }, position);
    if (header) tl.to(header, { yPercent: 0, opacity: 1, ease: "power2.out", duration: duration * 0.6 }, position + duration * 0.3);
    if (btn) tl.to(btn, { yPercent: 0, opacity: 1, ease: "power2.out", duration: duration * 0.5 }, position + duration * 0.4);
  },

  /**
   * pain-points-enter: reconstructs Figma state 2060:291 -> 2065:594.
   * The design stores static states, so the vectors below are derived from
   * the layer deltas on the 1440x810 artboard and scale with the stage.
   */
  "pain-points-enter": function (tl, target, position, duration, options) {
    var itemSelector = (options && options.itemSelector) || ".pain-points__item";
    var itemOffsets = (options && options.itemOffsets) || [];
    var header = target.querySelector((options && options.headerSelector) || ".pain-points__header");
    var items = target.querySelectorAll(itemSelector);
    var stage = document.querySelector(".stage");

    function offsetVars(offset) {
      return {
        x: function () {
          return md(window.innerWidth * ((offset && offset.xVw) || 0) / 100, options);
        },
        y: function () {
          var stageHeight = stage ? stage.clientHeight : window.innerHeight;
          return md(stageHeight * ((offset && offset.yStage) || 0) / 100, options);
        }
      };
    }

    if (options && options.mobile) {
      var mobileItemOffset = optionValue(options, "mobileItemOffset", 28);
      var mobileItemStagger = optionValue(options, "mobileItemStagger", 0.035);
      var mobileEase = optionValue(options, "mobileItemEase", "power2.out");

      // Reveal the mobile section progressively so it does not cover video abruptly.
      tl.fromTo(target,
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          ease: "power1.out",
          duration: optionValue(options, "mobileRevealDuration", 0.18)
        },
        position
      );

      // Mobile cards share one direction and one distance instead of using the
      // desktop Figma vectors. The stagger preserves readable rhythm in the list.
      if (header) {
        tl.fromTo(header,
          { x: 0, y: mobileItemOffset, autoAlpha: 0 },
          { x: 0, y: 0, autoAlpha: 1, ease: mobileEase, duration: duration },
          position
        );
      }

      items.forEach(function (item, i) {
        tl.fromTo(item,
          { x: 0, y: mobileItemOffset, autoAlpha: 0 },
          { x: 0, y: 0, autoAlpha: 1, ease: mobileEase, duration: duration },
          position + (i * mobileItemStagger)
        );
      });

      return;
    }

    if (header) {
      tl.set(target, { opacity: 1 }, position);

      tl.fromTo(header,
        Object.assign({ autoAlpha: 1 }, offsetVars((options && options.headerOffset) || {})),
        { x: 0, y: 0, autoAlpha: 1, ease: "power3.inOut", duration: duration, immediateRender: true },
        position);
    } else {
      tl.set(target, { opacity: 1 }, position);
    }

    items.forEach(function (item, i) {
      tl.fromTo(item,
        Object.assign({ autoAlpha: 1 }, offsetVars(itemOffsets[i] || {})),
        { x: 0, y: 0, autoAlpha: 1, ease: "power3.inOut", duration: duration, immediateRender: true },
        position);
    });
  },

  /** pain-points-exit: returns the grid to the dispersed Figma state. */
  "pain-points-exit": function (tl, target, position, duration, options) {
    var itemSelector = (options && options.itemSelector) || ".pain-points__item";
    var itemOffsets = (options && options.itemOffsets) || [];
    var header = target.querySelector((options && options.headerSelector) || ".pain-points__header");
    var items = target.querySelectorAll(itemSelector);
    var stage = document.querySelector(".stage");

    function offsetVars(offset) {
      return {
        x: function () {
          return md(window.innerWidth * ((offset && offset.xVw) || 0) / 100, options);
        },
        y: function () {
          var stageHeight = stage ? stage.clientHeight : window.innerHeight;
          return md(stageHeight * ((offset && offset.yStage) || 0) / 100, options);
        },
        autoAlpha: 1,
        ease: "power3.inOut",
        duration: duration
      };
    }

    if (options && options.mobile) {
      var mobileItemOffset = optionValue(options, "mobileItemOffset", 28);
      var mobileItemStagger = optionValue(options, "mobileItemStagger", 0.035);
      var mobileEase = optionValue(options, "mobileItemEase", "power2.out");
      var mobileFadeDelayRatio = Math.max(0, Math.min(1, optionValue(options, "exitFadeDelayRatio", 0)));
      var mobileFadeStart = position + (duration * mobileFadeDelayRatio);
      var mobileFadeDuration = duration * (1 - mobileFadeDelayRatio);

      // Reverse the mobile entrance: all cards leave in the same direction
      // instead of using the desktop dispersion vectors.
      tl.to(target, {
        autoAlpha: 0,
        ease: "power1.in",
        duration: mobileFadeDuration
      }, mobileFadeStart);

      if (header) {
        tl.to(header, {
          x: 0,
          y: mobileItemOffset,
          autoAlpha: 0,
          ease: mobileEase,
          duration: duration
        }, position);
      }

      items.forEach(function (item, i) {
        tl.to(item, {
          x: 0,
          y: mobileItemOffset,
          autoAlpha: 0,
          ease: mobileEase,
          duration: duration
        }, position + (i * mobileItemStagger));
      });

      return;
    }

    // Desktop keeps its existing dispersed movement. A fade ratio is opt-in
    // so existing pain-points behavior remains unchanged unless configured.
    if (options && options.exitFadeDelayRatio != null) {
      var fadeDelayRatio = Math.max(0, Math.min(1, options.exitFadeDelayRatio));
      tl.to(target, {
        autoAlpha: 0,
        ease: "power1.in",
        duration: duration * (1 - fadeDelayRatio)
      }, position + (duration * fadeDelayRatio));
    }

    if (header) {
      tl.to(header, offsetVars((options && options.headerOffset) || {}), position);
    }

    items.forEach(function (item, i) {
      tl.to(item, offsetVars(itemOffsets[i] || {}), position);
    });
  }
};

/**
 * Register a new animation preset at runtime.
 * @param {string} name
 * @param {Function} fn
 */
function registerPreset(name, fn) {
  if (typeof fn !== "function") {
    console.warn("[AnimEngine] registerPreset: fn must be a function");
    return;
  }
  PRESETS[name] = fn;
}

/* =====================================================
   CONFIG — SCENE_CONFIG (background groups)
   Each entry describes one background group and the
   sections that share it.
   ===================================================== */

var SCENE_CONFIG = [
  {
    id: "hero",
    bgType: "image",
    bgAsset: "https://cdn.shopify.com/s/files/1/0524/7326/6329/files/fondo-hero.webp?v=1779482969",
    sections: ["hero", "video", "pain-points"],
    overlay: true
  },
  {
    id: "spotlight",
    bgType: "video",
    bgAsset: "https://cdn.shopify.com/videos/c/o/v/3227893298104b928a24c4c5fb50d2cb.mp4",
    sections: ["reality", "visibility", "own-the-room"],
    overlay: true
  },
  {
    id: "particles",
    bgType: "video",
    bgAsset: "https://cdn.shopify.com/videos/c/o/v/5262f7dc9bfd4a3999d2495dab870ecf.mp4",
    sections: ["pilares", "overview", "bio", "before-after", "learn", "practica"],
    overlay: true
  },
  {
    id: "conference",
    bgType: "image",
    bgAsset: "https://cdn.shopify.com/s/files/1/0524/7326/6329/files/91b357d1608e857ae0c548dce9c6defa34f31014_4e1f4605-f97c-42d8-bead-ca974f11e113.webp?v=1786412858",
    sections: ["includes", "testimonials", "pricing"],
    overlay: true
  },
  {
    id: "stadium",
    bgType: "image",
    bgAsset: "https://cdn.shopify.com/s/files/1/0524/7326/6329/files/54a97538398a6d459995d3c314cfc106883af0c6.webp?v=1785887218",
    sections: ["final-cta"],
    overlay: true
  }
];

/* =====================================================
   CONFIG — SECTION_CONFIG (scroll order)
   ===================================================== */

var SECTION_CONFIG = [
  { id: "hero", group: "hero", enter: null, exit: "custom-hero", params: { exitDuration: 0.3 } },
  { id: "video", group: "hero", enter: "video-enter", exit: "fade-down", params: { distance: 100, enterDelay: 0.1, enterDuration: 0.3, exitDuration: 0.4 } },
  {
    id: "pain-points", group: "hero", enter: "pain-points-enter", exit: "pain-points-exit",
    params: {
      enterDelay: 0.1,
      enterDuration: 0.4,
      exitDuration: 0.4,
      mobileRevealDuration: 0.18,
      mobileDistanceScale: 0.35,
      mobileItemOffset: 28,
      mobileItemStagger: 0.035,
      itemSelector: ".pain-points__item",
      headerSelector: ".pain-points__header",
      // Relative deltas measured from Figma 2060:291 to 2065:594 (1440x810).
      headerOffset: { xVw: 0, yStage: 88.15 },
      itemOffsets: [
        { xVw: -13.47, yStage: 88.15 },
        { xVw: 0, yStage: 110.12 },
        { xVw: 13.47, yStage: 88.15 },
        { xVw: -27.01, yStage: 89.88 },
        { xVw: 0, yStage: 134.57 },
        { xVw: 29.65, yStage: 89.88 }
      ]
    }
  },
  { id: "reality", group: "spotlight", enter: "fade-in", exit: "reality-container-exit", params: { enterDelay: 0.1, enterDuration: 0.4, distance: 8, motionDuration: 0.45, exitDuration: 0.4 } },
  { id: "visibility", group: "spotlight", enter: "visibility-container-enter", exit: "visibility-container-exit", params: { enterDelay: 0.3, enterOffset: -0.5, startViewport: 0.2, motionDuration: 0.4, fadeDelayRatio: 0.60, exitScale: 2.2, exitDuration: 0.4, exitFadeDelayRatio: 0 } },
  {
    id: "own-the-room", group: "spotlight", enter: "staged-fade-up", exit: "fade-down",
    params: {
      enterDelay: 0.1,
      enterDuration: 0.4,
      exitDuration: 0.4,
      distance: 48,
      phaseStagger: 0.32,
      phases: [".own-the-room__header", ".own-the-room__quote"]
    }
  },
  {
    id: "pilares", group: "particles", enter: "staged-fade-up", exit: "fade-down",
    params: {
      enterDelay: 0.1,
      enterDuration: 0.35,
      exitDuration: 0.4,
      distance: 40,
      phaseStagger: 0.22,
      itemStagger: 0.07,
      phases: [".pilares__heading", ".pilares__item", ".pilares__quote"]
    }
  },
  { id: "overview", group: "particles", enter: "overview-container-enter", exit: "fade-down", params: { enterDelay: 0.1, enterDuration: 0.4, startScale: 0.75, startDelayRatio: 0.2, exitDuration: 0.4, exitFadeDelayRatio: 0 } },
  { id: "bio", group: "particles", enter: "bio-columns-enter", exit: "fade-down", params: { enterDelay: 0.2, enterDuration: 0.4, bioGroupStagger: 0.12, exitDuration: 0.4 } },
  { id: "before-after", group: "particles", enter: "before-after-enter", exit: "fade-down", params: { enterDelay: 0.2, enterDuration: 0.4, beforeAfterCardDelay: 0.12, beforeAfterArrowDuration: 0.18, exitDuration: 0.4 } },
  { id: "learn", group: "particles", enter: "pain-points-enter", exit: "pain-points-exit", params: {
    enterDelay: 0.1,
    enterDuration: 0.4,
    exitDuration: 0.3,
    exitFadeDelayRatio: 0,
    mobileRevealDuration: 0.18,
    mobileDistanceScale: 0.35,
    mobileItemOffset: 28,
    mobileItemStagger: 0.035,
    itemSelector: ".learn__card",
    headerSelector: ".learn__title",
    headerOffset: { xVw: 0, yStage: 88.15 },
    itemOffsets: [
      { xVw: -13.47, yStage: 88.15 },
      { xVw: 0,      yStage: 110.12 },
      { xVw: 13.47,  yStage: 88.15 },
      { xVw: -27.01, yStage: 89.88 },
      { xVw: 0,      yStage: 134.57 },
      { xVw: 29.65,  yStage: 89.88 }
    ]
  } },
  {
    id: "practica", group: "particles", enter: "staged-fade-up", exit: "fade-down",
    params: {
      enterDelay: 0.1,
      enterDuration: 0.35,
      exitDuration: 0.4,
      distance: 44,
      phaseStagger: 0.26,
      itemStagger: 0.1,
      phases: [".practica__header", ".practica__card"]
    }
  },
  { id: "includes", group: "conference", enter: "fade-up", exit: "scale-out", params: { enterDelay: 0.1, enterDuration: 0.4, exitDuration: 0.4, exitScale: 0, exitFadeDelayRatio: 0 } },
  { id: "testimonials", group: "conference", enter: "fade-up", exit: "scale-out", params: { enterDelay: 0.2, enterDuration: 0.4, exitDuration: 0.4 } },
  { id: "pricing", group: "conference", enter: "scale-in", exit: "fade-down", params: { enterDelay: 0.2, enterDuration: 0.4, exitDuration: 0.4 } },
  { id: "final-cta", group: "stadium", enter: "fade-up", exit: null, params: { enterDelay: 0.3, enterDuration: 0.4 } }
];

/* =====================================================
   HELPER — groupForSection
   ===================================================== */

/**
 * Returns the background group id whose sections[] contains the given section.
 * @param {string} sectionId
 * @returns {string|null}
 */
function groupForSection(sectionId) {
  for (var i = 0; i < SCENE_CONFIG.length; i++) {
    if (SCENE_CONFIG[i].sections.indexOf(sectionId) !== -1) {
      return SCENE_CONFIG[i].id;
    }
  }
  console.warn("[AnimEngine] No background group found for section '" + sectionId + "'");
  return null;
}

/* =====================================================
   HELPER — addBackgroundCrossfade
   ===================================================== */

/**
 * Crossfades the outgoing background group layer (1→0) and the incoming one (0→1).
 * @param {gsap.core.Timeline} tl
 * @param {string} fromGroup
 * @param {string} toGroup
 * @param {number} position - timeline time
 * @param {number} duration
 */
function addBackgroundCrossfade(tl, fromGroup, toGroup, position, duration) {
  var fromLayer = document.querySelector(".stage__bg--" + fromGroup);
  var toLayer = document.querySelector(".stage__bg--" + toGroup);

  if (!toLayer) {
    console.warn("[AnimEngine] Background layer .stage__bg--" + toGroup + " not found");
    return;
  }
  if (fromLayer) {
    tl.fromTo(fromLayer,
      { opacity: 1 },
      { opacity: 0, ease: "power1.inOut", duration: duration, immediateRender: false },
      position);
  }
  tl.fromTo(toLayer,
    { opacity: 0 },
    { opacity: 1, ease: "power1.inOut", duration: duration, immediateRender: false },
    position);
}

/* =====================================================
   DEBUG — per-section ScrollTrigger markers
   ===================================================== */

function clearSectionDebugMarkers() {
  DEBUG_SECTION_MARKERS.forEach(function (marker) {
    marker.kill();
  });
  DEBUG_SECTION_MARKERS.length = 0;
}

function addSectionDebugMarkers(tl) {
  clearSectionDebugMarkers();

  if (!DEBUG_SCROLLTRIGGER || !tl || !tl.scrollTrigger) {
    return;
  }

  var colors = ["#00ff88", "#49c6ff", "#ffc857", "#ff6b6b"];

  SECTION_CONFIG.forEach(function (descriptor, index) {
    var nextDescriptor = SECTION_CONFIG[index + 1];
    var color = colors[index % colors.length];

    var marker = ScrollTrigger.create({
      trigger: document.body,
      start: function () {
        return tl.scrollTrigger.labelToScroll(descriptor.id);
      },
      end: function () {
        return nextDescriptor
          ? tl.scrollTrigger.labelToScroll(nextDescriptor.id)
          : tl.scrollTrigger.end;
      },
      id: "section-" + descriptor.id,
      markers: {
        startColor: color,
        endColor: color,
        indent: 20 + (index * 8),
        fontSize: "11px"
      }
    });

    DEBUG_SECTION_MARKERS.push(marker);
  });
}

function debugElement(tag, styles, text) {
  var element = document.createElement(tag);
  element.style.cssText = styles;
  if (text != null) element.textContent = text;
  return element;
}

function clearTimelineDebugPanel() {
  if (DEBUG_TIMELINE_PANEL && DEBUG_TIMELINE_PANEL.panel.parentNode) {
    DEBUG_TIMELINE_PANEL.panel.parentNode.removeChild(DEBUG_TIMELINE_PANEL.panel);
  }
  DEBUG_TIMELINE_PANEL = null;
}

function openTimelinePictureInPicture(panel) {
  if (!window.documentPictureInPicture) {
    console.info("[AnimEngine] Document Picture-in-Picture is not supported; keeping the debug panel in-page.");
    return;
  }

  if (DEBUG_TIMELINE_PIP_WINDOW && !DEBUG_TIMELINE_PIP_WINDOW.closed) {
    DEBUG_TIMELINE_PIP_WINDOW.focus();
    return;
  }

  window.documentPictureInPicture.requestWindow({ width: 780, height: 430 })
    .then(function (pipWindow) {
      DEBUG_TIMELINE_PIP_WINDOW = pipWindow;
      pipWindow.document.title = "GSAP Timeline Debug";
      pipWindow.document.body.style.margin = "0";
      pipWindow.document.body.style.minHeight = "100vh";
      pipWindow.document.body.style.background = "#070a10";
      pipWindow.document.body.appendChild(panel);

      pipWindow.addEventListener("pagehide", function () {
        if (panel.parentNode === pipWindow.document.body) {
          document.body.appendChild(panel);
        }
        DEBUG_TIMELINE_PIP_WINDOW = null;
      });
    })
    .catch(function (error) {
      console.warn("[AnimEngine] Could not open timeline Picture-in-Picture", error);
    });
}

function getDebugPhaseTiming(descriptor, index) {
  var params = descriptor.params || {};
  var enterStart = null;
  var enterDuration = null;
  var exitStart = null;
  var exitDuration = null;

  if (index > 0 && descriptor.enter) {
    enterStart = (index - 1) + optionValue(params, "enterDelay", DEFAULT_TIMING.enterDelay) + (params.enterOffset || 0);
    enterDuration = optionValue(params, "enterDuration", DEFAULT_TIMING.enterDuration);

    if (descriptor.enter === "overview-container-enter") {
      enterStart += enterDuration * optionValue(params, "startDelayRatio", 0);
    }

    if (descriptor.enter === "bio-columns-enter") {
      enterDuration += optionValue(params, "bioGroupStagger", 0.12) * 2;
    }

    if (descriptor.enter === "before-after-enter") {
      enterDuration += optionValue(params, "beforeAfterCardDelay", 0.12);
      enterDuration += optionValue(params, "beforeAfterArrowDuration", 0.18);
    }
  }

  if (index < SECTION_CONFIG.length - 1 && descriptor.exit) {
    exitStart = index + optionValue(params, "exitOffset", DEFAULT_TIMING.exitOffset);
    exitDuration = optionValue(params, "exitDuration", DEFAULT_TIMING.exitDuration);
  }

  return {
    enterStart: enterStart,
    enterDuration: enterDuration,
    exitStart: exitStart,
    exitDuration: exitDuration
  };
}

function addTimelineDebugBar(track, start, duration, color, label, totalDuration) {
  if (start == null || duration == null) return;

  var left = Math.max(0, Math.min(100, (start / totalDuration) * 100));
  var width = Math.max(0.5, (duration / totalDuration) * 100);
  var bar = debugElement("div",
    "position:absolute;left:" + left + "%;top:3px;width:" + width + "%;height:16px;" +
    "border-radius:999px;background:" + color + ";opacity:.9;box-shadow:0 0 8px " + color + ";",
    "");
  bar.title = label + "  t=" + formatDebugTime(start) + " → " + formatDebugTime(start + duration);
  track.appendChild(bar);
}

function formatDebugTime(value) {
  return Number(value).toFixed(2);
}

function updateTimelineDebug(tl) {
  if (!DEBUG_TIMELINE_PANEL) return;

  var totalDuration = tl.duration();
  var currentTime = tl.time();
  var progress = totalDuration ? currentTime / totalDuration : 0;
  var activeIndex = Math.max(0, Math.min(SECTION_CONFIG.length - 1, Math.round(currentTime)));
  var activeId = SECTION_CONFIG[activeIndex] ? SECTION_CONFIG[activeIndex].id : "-";

  DEBUG_TIMELINE_PANEL.readout.textContent =
    "t=" + formatDebugTime(currentTime) + " / " + formatDebugTime(totalDuration) +
    "   progress=" + (progress * 100).toFixed(1) + "%   active=" + activeId;
  DEBUG_TIMELINE_PANEL.playhead.style.left = Math.max(0, Math.min(100, progress * 100)) + "%";
}

function addTimelineDebugPanel(tl) {
  clearTimelineDebugPanel();

  if (!DEBUG_SCROLLTRIGGER || !tl) return;

  var totalDuration = tl.duration();
  var panel = debugElement("aside",
    "position:fixed;right:12px;bottom:12px;z-index:10000;width:min(760px,calc(100vw - 24px));" +
    "max-height:60vh;overflow:auto;padding:12px 14px;border:1px solid rgba(255,255,255,.2);" +
    "border-radius:10px;background:rgba(7,10,16,.92);color:#e8edf3;font:11px/1.3 ui-monospace,SFMono-Regular,Menlo,monospace;" +
    "box-shadow:0 10px 40px rgba(0,0,0,.35);pointer-events:auto;",
    "");

  var controls = debugElement("div", "display:flex;align-items:center;gap:8px;margin-bottom:8px;", "");
  var heading = debugElement("div", "font-weight:700;color:#fff;flex:1;", "GSAP TIMELINE DEBUG");
  var pipButton = debugElement("button", "border:1px solid rgba(255,255,255,.35);border-radius:5px;padding:3px 7px;background:#1d2939;color:#e8edf3;font:inherit;cursor:pointer;", "PiP");
  pipButton.type = "button";
  pipButton.title = "Abrir el timeline en una ventana Picture-in-Picture";
  pipButton.addEventListener("click", function () {
    openTimelinePictureInPicture(panel);
  });
  controls.appendChild(heading);
  controls.appendChild(pipButton);

  var readout = debugElement("div", "color:#8fe3ff;margin-bottom:10px;", "");
  var ruler = debugElement("div", "position:relative;height:30px;margin-left:112px;border-bottom:1px solid rgba(255,255,255,.35);", "");
  var playhead = debugElement("div", "position:absolute;top:0;bottom:-1px;left:0;width:2px;background:#fff;box-shadow:0 0 8px #fff;", "");
  ruler.appendChild(playhead);

  SECTION_CONFIG.forEach(function (descriptor, index) {
    var label = debugElement("div", "position:absolute;left:" + ((index / totalDuration) * 100) + "%;top:0;transform:translateX(-50%);color:#aeb8c4;white-space:nowrap;", descriptor.id + " " + formatDebugTime(index));
    ruler.appendChild(label);
  });

  panel.appendChild(controls);
  panel.appendChild(readout);
  panel.appendChild(ruler);

  SECTION_CONFIG.forEach(function (descriptor, index) {
    var timing = getDebugPhaseTiming(descriptor, index);
    var row = debugElement("div", "display:flex;align-items:center;min-height:24px;", "");
    var rowLabel = debugElement("div", "width:104px;flex:0 0 104px;color:#cbd5e1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;", descriptor.id);
    var track = debugElement("div", "position:relative;flex:1;height:22px;border-top:1px solid rgba(255,255,255,.1);", "");

    addTimelineDebugBar(track, timing.enterStart, timing.enterDuration, "#55d6ff", "enter-" + descriptor.id, totalDuration);
    addTimelineDebugBar(track, timing.exitStart, timing.exitDuration, "#49ef7d", "exit-" + descriptor.id, totalDuration);

    row.appendChild(rowLabel);
    row.appendChild(track);
    panel.appendChild(row);
  });

  document.body.appendChild(panel);
  DEBUG_TIMELINE_PANEL = { panel: panel, readout: readout, playhead: playhead };
  updateTimelineDebug(tl);
}

/* =====================================================
   ENGINE — buildStage
   Builds the single master timeline + one pinned ScrollTrigger.
   ===================================================== */

/**
 * @param {{ mobile: boolean }} ctx
 * @returns {gsap.core.Timeline|null}
 */
function setInteractiveSection(stageEl, activeIndex) {
  var sections = stageEl.querySelectorAll(".stage__content > section");
  var safeIndex = Math.max(0, Math.min(activeIndex, sections.length - 1));

  sections.forEach(function (section, index) {
    section.classList.toggle("is-active", index === safeIndex);
  });

  // The hero artwork contains a desktop pain-points collage. On mobile,
  // isolate the live responsive cards while that section is active.
  var activeSection = sections[safeIndex];
  stageEl.classList.toggle(
    "is-pain-points-active",
    !!activeSection && activeSection.classList.contains("pain-points")
  );
}

/* --- Quien recibe los clicks -------------------------------------------
   Las secciones estan apiladas y solo la que tiene .is-active recibe punteros.
   Resolverlo con Math.round(time) en cada update cambiaba el dueno de los
   clicks en cuanto el timeline pasaba la mitad de una transicion: mientras la
   escena todavia se estaba acomodando (scrub 0.5 + snap), los botones que el
   usuario veia ya no respondian. Aqui el cambio solo ocurre cuando el timeline
   esta cerca de una etiqueta (SETTLE_THRESHOLD) o cuando se quedo quieto
   (IDLE_DELAY), asi que la seccion visible conserva la interaccion durante todo
   el trayecto. */
var SETTLE_THRESHOLD = 0.25;
var IDLE_DELAY = 140;
var interactiveIndex = 0;
var interactiveIdleTimer = 0;

/* Marca como apagada toda escena cuya opacidad real sea ~0. Sus controles
   siguen apilados sobre la escena visible y, sin esto, se quedan con el click
   (medido: el <video> de testimonials y los CTA de pricing interceptaban los
   botones del hero). Se lee la opacidad calculada, no el tiempo del timeline,
   asi que coincide siempre con lo que el usuario ve. */
var dimmedFrame = 0;

function refreshDimmedSections(stageEl) {
  if (dimmedFrame) return;

  dimmedFrame = window.requestAnimationFrame(function () {
    dimmedFrame = 0;
    var sections = stageEl.querySelectorAll(".stage__content > section");

    sections.forEach(function (section) {
      var opacity = parseFloat(window.getComputedStyle(section).opacity);
      section.classList.toggle("is-dimmed", !(opacity > 0.05));
    });
  });
}

function commitInteractiveSection(stageEl, index) {
  if (index === interactiveIndex) return;
  interactiveIndex = index;
  setInteractiveSection(stageEl, index);
}

function updateInteractiveSection(stageEl, timeline) {
  var time = timeline.time();
  var nearest = Math.round(time);

  if (Math.abs(time - nearest) <= SETTLE_THRESHOLD) {
    commitInteractiveSection(stageEl, nearest);
  }

  // Si el usuario suelta el scroll a medio camino, el snap puede no llegar:
  // tras un momento sin updates se le entrega la interaccion a la mas cercana.
  window.clearTimeout(interactiveIdleTimer);
  interactiveIdleTimer = window.setTimeout(function () {
    commitInteractiveSection(stageEl, Math.round(timeline.time()));
  }, IDLE_DELAY);
}

function playVideoFromThumbnail(stageEl) {
  var videoPlayer = stageEl.querySelector(".video__player");
  if (!videoPlayer) return;

  // Keep the existing muted behavior so playback is allowed from the click gesture.
  videoPlayer.muted = true;
  var playPromise = videoPlayer.play();

  if (playPromise && typeof playPromise.catch === "function") {
    playPromise.catch(function (error) {
      console.warn("[AnimEngine] Video playback could not start", error);
    });
  }
}

function bindHeroThumbnail(stageEl, onNavigate) {
  var thumbnailEl = stageEl.querySelector(".hero__thumbnail");
  if (!thumbnailEl) return;

  thumbnailEl.addEventListener("click", function (event) {
    playVideoFromThumbnail(stageEl);
    if (onNavigate) onNavigate(event);
  });
}

function setTestimonialPlayOverlay(video, isPlaying) {
  var figure = video && video.parentNode;
  if (!figure || !figure.classList) return;

  figure.classList.toggle("is-playing", isPlaying);
}

function promoteTestimonialVideo(container, selectedVideo) {
  var featured = container.querySelector(".testimonials__featured");
  var featuredVideo = featured && featured.querySelector("video");
  var selectedSlot = selectedVideo && selectedVideo.parentNode;

  if (!featured || !featuredVideo || !selectedSlot || selectedVideo === featuredVideo) {
    return;
  }

  // Keep only the selected testimonial playing so audio does not overlap.
  container.querySelectorAll("video").forEach(function (video) {
    if (video !== selectedVideo) {
      video.pause();
    }
  });

  // The featured video has native controls; thumbnails stay clean and clickable.
  selectedVideo.setAttribute("controls", "");
  featuredVideo.removeAttribute("controls");

  // Swap the video nodes so the previous featured testimonial keeps its slot.
  featured.replaceChild(selectedVideo, featuredVideo);
  selectedSlot.insertBefore(featuredVideo, selectedSlot.firstChild);
  selectedSlot.classList.remove("is-playing");
  featured.classList.add("is-playing");
}

function bindTestimonialVideoPromotion() {
  var container = document.querySelector(".testimonials");
  if (!container) return;

  container.querySelectorAll("video").forEach(function (video) {
    video.addEventListener("click", function () {
      if (video.controls) return;

      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(function (error) {
          console.warn("[AnimEngine] Testimonial thumbnail playback could not start", error);
        });
      }
    });
    video.addEventListener("play", function () {
      setTestimonialPlayOverlay(video, true);
      promoteTestimonialVideo(container, video);
    });
    video.addEventListener("pause", function () {
      setTestimonialPlayOverlay(video, false);
    });
    video.addEventListener("ended", function () {
      setTestimonialPlayOverlay(video, false);
    });
  });
}

function getHeaderHeight(stageEl) {
  var siteHeader = document.querySelector("#SiteHeader");
  var headerRoot = siteHeader && siteHeader.closest('[data-section-type="header"]');
  var isSticky = siteHeader && siteHeader.classList.contains("site-header--stuck");
  var measuredHeight = isSticky && siteHeader
    ? siteHeader.getBoundingClientRect().height
    : headerRoot && headerRoot.getBoundingClientRect().height;
  var fallbackHeight = parseInt(getComputedStyle(stageEl).getPropertyValue("--header-height"), 10) || 0;

  return Math.round(measuredHeight || fallbackHeight);
}

function buildStage(ctx) {
  ctx = ctx || { mobile: false };

  var stageEl = document.querySelector(".stage");
  if (!stageEl) {
    console.warn("[AnimEngine] .stage not found");
    return null;
  }

  var N = SECTION_CONFIG.length;
  var duration = N - 1; // one time unit per transition
  // Porcentaje del alto del stage que cuesta pasar de una seccion a la siguiente.
  var scrollPerSection = ctx.mobile ? 65 : 100;

  // Only the first section is interactive at the initial timeline position.
  interactiveIndex = 0;
  setInteractiveSection(stageEl, 0);
  refreshDimmedSections(stageEl);

  // On mobile the content sections are stacked, so hide every non-hero
  // section before the timeline reaches its entrance label. Desktop keeps its
  // original initialization behavior.
  if (ctx.mobile) {
    var contentSections = stageEl.querySelectorAll(".stage__content > section");
    contentSections.forEach(function (section, index) {
      gsap.set(section, { opacity: index === 0 ? 1 : 0 });
    });
  }

  // Force initial background layer state: only the first group's layer visible.
  SCENE_CONFIG.forEach(function (group, idx) {
    var layer = stageEl.querySelector(".stage__bg--" + group.id);
    if (layer) gsap.set(layer, { opacity: idx === 0 ? 1 : 0 });
  });

  var headerHeight = getHeaderHeight(stageEl);
  stageEl.style.setProperty("--header-height", headerHeight + "px");
  var overlayEl = stageEl.querySelector(".stage__overlay");

  if (overlayEl) {
    gsap.set(overlayEl, { opacity: 1 });
  }

  var tl = gsap.timeline({
    onUpdate: function () {
      // Each label represents the section that should receive interaction.
      updateInteractiveSection(stageEl, this);
      refreshDimmedSections(stageEl);
      updateTimelineDebug(this);
    },
    scrollTrigger: {
      trigger: stageEl,
      start: function () {
        return "top top+=" + getHeaderHeight(stageEl);
      },
      // Cada transicion consumia un viewport completo de scroll (medido: 801px
      // con innerHeight 801). En movil eso es mas de lo que recorre un swipe de
      // pulgar, asi que hacian falta dos gestos por seccion. Desktop se queda
      // con su recorrido original.
      end: "+=" + (duration * scrollPerSection) + "%",
      // El scrub numerico deja al playhead alcanzando al scroll despues de
      // soltar el dedo. En movil respondemos directo; desktop conserva su
      // sensacion original.
      scrub: ctx.mobile ? true : 0.5,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      snap: {
        snapTo: "labelsDirectional",
        // Con duracion 0 el salto dependia de que ScrollTrigger viera el scroll
        // detenido; con la inercia de iOS se quedaba a media unidad. Un tween
        // corto y sin proyeccion de velocidad siempre aterriza en la etiqueta.
        duration: { min: 0.15, max: 0.35 },
        ease: "power1.inOut",
        inertia: false,
        delay: 0.05
      },
      markers: DEBUG_SCROLLTRIGGER
    }
  });

  var prevGroup = null;

  for (var i = 0; i < N; i++) {
    var descriptor = SECTION_CONFIG[i];
    var sectionEl = stageEl.querySelector(".stage__content > ." + descriptor.id);

    if (!sectionEl) {
      console.warn("[AnimEngine] Section ." + descriptor.id + " not found in .stage__content");
      // still place a label so timing stays aligned
      tl.addLabel(descriptor.id, i);
      prevGroup = groupForSection(descriptor.id);
      continue;
    }

    // Label for this section at integer time i
    tl.addLabel(descriptor.id, i);

    // Merge mobile flag into preset params
    var params = Object.assign({}, descriptor.params, { mobile: ctx.mobile });

    var thisGroup = groupForSection(descriptor.id);

    // Background crossfade during the transition INTO this section (T_{i-1})
    if (i > 0 && thisGroup && prevGroup && thisGroup !== prevGroup) {
      addBackgroundCrossfade(tl, prevGroup, thisGroup, i - 1, 1);
    }

    // Remove the shared dark overlay while the final CTA is entering.
    // The tween reverses automatically when scrolling back to pricing.
    if (descriptor.id === "final-cta" && overlayEl) {
      tl.to(overlayEl,
        { opacity: 0, ease: "power1.inOut", duration: 1 },
        i - 1);
    }

    // Enter animation (every section except the first, which starts visible)
    if (i > 0 && descriptor.enter) {
      var enterPreset = PRESETS[descriptor.enter];
      if (!enterPreset) {
        console.warn('[AnimEngine] Preset "' + descriptor.enter + '" not defined');
      } else {
        var enterDelay = optionValue(params, "enterDelay", DEFAULT_TIMING.enterDelay);
        var enterDuration = optionValue(params, "enterDuration", DEFAULT_TIMING.enterDuration);
        var enterPosition = (i - 1) + enterDelay + (params.enterOffset || 0);
        enterPreset(tl, sectionEl, enterPosition, enterDuration, params);
      }
    }

    // Exit animation (every section except the last)
    if (i < N - 1 && descriptor.exit) {
      var exitPreset = PRESETS[descriptor.exit];
      if (!exitPreset) {
        console.warn('[AnimEngine] Preset "' + descriptor.exit + '" not defined');
      } else {
        var exitOffset = optionValue(params, "exitOffset", DEFAULT_TIMING.exitOffset);
        var exitDuration = optionValue(params, "exitDuration", DEFAULT_TIMING.exitDuration);
        exitPreset(tl, sectionEl, i + exitOffset, exitDuration, params);
      }
    }

    prevGroup = thisGroup;
  }

  addSectionDebugMarkers(tl);
  if (!ctx.mobile) {
    addTimelineDebugPanel(tl);
  }

  bindHeroThumbnail(stageEl, function (event) {
    event.preventDefault();

    var targetScroll = tl.scrollTrigger.labelToScroll("video");
    // Smooth scroll nativo entra en conflicto con normalizeScroll y snap;
    // el salto directo llega al label exacto en un solo click.
    window.scrollTo({
      top: targetScroll,
      behavior: "auto"
    });
  });

  return tl;
}

/* =====================================================
   ENGINE — initReducedMotion (minimal; full version in Phase D)
   ===================================================== */

function initReducedMotion() {
  ScrollTrigger.getAll().forEach(function (t) { t.kill(); });

  var stage = document.querySelector(".stage");
  if (stage) {
    stage.style.height = "auto";
    stage.style.overflow = "visible";
  }

  document.querySelectorAll(".stage__content").forEach(function (content) {
    content.style.position = "static";
  });

  document.querySelectorAll(".stage__content > section").forEach(function (section) {
    section.style.position = "relative";
    section.style.opacity = "1";
    section.style.visibility = "visible";
    section.style.transform = "none";
    section.style.minHeight = "100dvh";
  });

  bindHeroThumbnail(stage);

  // Show only each group's background where its sections appear: simplest is
  // to show the first (hero) background and hide the rest (no crossfades).
  document.querySelectorAll(".stage__bg").forEach(function (bg, idx) {
    bg.style.opacity = idx === 0 ? "1" : "0";
  });
}

/* =====================================================
   ENGINE — initAnimationSystem
   ===================================================== */

function initAnimationSystem() {
  bindTestimonialVideoPromotion();

  var motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  if (motionQuery.matches) {
    initReducedMotion();
    return;
  }

  var isMobile = window.matchMedia("(max-width: 767px)").matches;

  // Soporte del pin movil. Desactiva solo el momentum tactil del normalizer
  // para que la pagina se detenga al soltar el dedo, conservando la
  // estabilizacion del pin que normalizeScroll aporta en moviles.
  ScrollTrigger.config({ ignoreMobileResize: true });
  ScrollTrigger.normalizeScroll(isMobile ? {
    type: "touch,wheel",
    momentum: function () {
      return 0;
    }
  } : true);

  var timeline = buildStage({ mobile: isMobile });
  var stageEl = document.querySelector(".stage");
  var headerSyncFrame = 0;

  function syncHeaderHeight() {
    if (!stageEl || !timeline || !timeline.scrollTrigger) return false;

    var nextHeight = getHeaderHeight(stageEl);
    var currentHeight = parseInt(getComputedStyle(stageEl).getPropertyValue("--header-height"), 10) || 0;

    if (!nextHeight || nextHeight === currentHeight) return false;

    stageEl.style.setProperty("--header-height", nextHeight + "px");
    timeline.scrollTrigger.refresh();
    return true;
  }

  function scheduleHeaderSync() {
    if (headerSyncFrame) return;

    headerSyncFrame = requestAnimationFrame(function () {
      headerSyncFrame = requestAnimationFrame(function () {
        headerSyncFrame = 0;
        syncHeaderHeight();
      });
    });
  }

  syncHeaderHeight();
  window.addEventListener("scroll", scheduleHeaderSync, { passive: true });

  // Refresh only on WIDTH changes (ignore mobile address-bar height changes)
  var lastWidth = window.innerWidth;
  window.addEventListener("resize", function () {
    var w = window.innerWidth;
    if (w !== lastWidth) {
      lastWidth = w;
      syncHeaderHeight();
      ScrollTrigger.refresh();
    }
  });
}

/* =====================================================
   INIT
   ===================================================== */

initAnimationSystem();
